import { useSDKContext } from '@/sdk/hooks/useSDKContext';
import { NoResults } from '@/sdk/pages/NoResults';
import type { Theme, WithStyles } from '@/utils/material';
import {
  Box,
  Card,
  CircularProgress,
  createStyles,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
  withTheme,
} from '@/utils/material';
import { GAEvent } from '@/utils/utils';
import { getBathroomCount, getLocationParams, Menu } from '@propify-tenant-client/common';
import { type ActivePlace, UnitHooks } from '@propify-tenant-client/services';
import {
  getPoints,
  getSdkUnitListClassName,
  getUnitFilter,
  Toolbar,
  type UnitFilter,
  UnitList,
  UnitsMap,
} from '@propify-tenant-client/units';
import classNames from 'classnames';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import queryString from 'query-string';
import { type CSSProperties, type FC, memo, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

type SortDirection = 'ASC' | 'DES';
type Sort = {
  key: 'listedRent' | 'bedroomCount' | 'bathroomCount' | 'squareFeet';
  direction: SortDirection;
};

const MAX_UNITS_IN_LIST = 100;

const styles = (theme: Theme) =>
  createStyles({
    main: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        overflow: 'auto',
      },
    },
    mapAndList: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.up('md')]: {
        overflow: 'auto',
      },
    },
    list: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100vw',
      [theme.breakpoints.up('md')]: {
        minWidth: 700,
        maxWidth: 700,
        overflow: 'auto',
      },
    },
    map: {
      flex: 1,
    },
    image: {
      height: '100px',
      width: '100%',
      textAlign: 'center',
      margin: 'auto',
    },
    header: {
      position: 'sticky',
      top: 0,
      margin: '0 0.5em',
      zIndex: 10,
    },
    headerTitle: {
      display: 'flex',
      margin: '0.5em',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 10,
      [theme.breakpoints.down('xs')]: {
        display: 'block',
      },
    },
    headerSubtitle: {
      [theme.breakpoints.down('xs')]: {
        marginBottom: '0.5em',
      },
    },
  });

type Props = WithStyles<typeof styles> & {
  showMap: boolean;
};

interface SortOption {
  value: string;
  sort: Sort;
  label: string;
}

const getSortOptions = (): SortOption[] => [
  {
    value: 'displayRent-ASC',
    sort: { key: 'listedRent', direction: 'ASC' },
    label: 'Rent (Low to High)',
  },
  {
    value: 'displayRent-DES',
    sort: { key: 'listedRent', direction: 'DES' },
    label: 'Rent (High to Low)',
  },
  {
    value: 'bedroomCount-DES',
    sort: { key: 'bedroomCount', direction: 'DES' },
    label: 'Bedrooms',
  },
  {
    value: 'bathroomCount-DES',
    sort: { key: 'bathroomCount', direction: 'DES' },
    label: 'Bathrooms',
  },
  {
    value: 'squareFeet-DES',
    sort: { key: 'squareFeet', direction: 'DES' },
    label: 'Square Feet',
  },
];

const defaultSort: Sort = { key: 'listedRent', direction: 'ASC' };

type SdkSearchParams = {
  minDisplayRent?: string;
  maxDisplayRent?: string;
  minBedroomCount?: string;
  minBathroomCount?: string;
  availableNow?: string;
  state?: string;
};

const App: FC<Props> = ({ classes, showMap }) => {
  const [activePlace, setActivePlace] = useState<ActivePlace>();
  const [filter, setFilter] = useState<UnitFilter>();
  const [mapData, setMapData] = useState<Record<string, any>>();
  const [sortBy, setSortBy] = useState<Sort>(defaultSort);
  const location = useLocation();
  const { config } = useSDKContext();

  const {
    data: units,
    isLoading: blankPage,
    isFetching: loading,
  } = UnitHooks.useUnits(config?.unitParameters ?? {});

  const filteredUnits = useMemo(() => (units ?? []).filter(getUnitFilter(filter)), [filter, units]);

  const visibleUnits = useMemo(() => {
    const { key, direction } = sortBy;
    const dir = direction === 'ASC' ? 1 : -1;
    const isByBathroom = key === 'bathroomCount';

    return filteredUnits
      .filter((unit) => {
        if (!mapData?.bounds) {
          return true;
        }

        return (
          !!unit.property.address.location &&
          mapData?.bounds?.contains({
            lat: unit.property.address.location?.latitude,
            lng: unit.property.address.location?.longitude,
          })
        );
      })
      .sort(
        isByBathroom
          ? (a, b) => dir * (getBathroomCount(a) - getBathroomCount(b))
          : (a, b) => dir * ((Number(get(a, key)) || 0) - (Number(get(b, key)) || 0)),
      );
  }, [filteredUnits, mapData?.bounds, sortBy]);

  const points = useMemo(() => getPoints(filteredUnits), [filteredUnits]);

  const stateKeys: string[] = useMemo(
    () => [...new Set((units ?? []).map((unit) => unit.property.address.state))],
    [units],
  );

  const onSearch = () => {
    const redirect = config?.redirectSearchTo;

    const params: SdkSearchParams = pickBy(
      {
        minDisplayRent: filter?.displayRent?.min,
        maxDisplayRent: filter?.displayRent?.max,
        minBedroomCount: filter?.bedroomCount?.min,
        minBathroomCount: filter?.bathroomCount?.min,
        availableNow: filter?.availableNow,
        state: filter?.state,
      },
      (param) => param,
    );

    window.location.href = `${redirect}#/?${queryString.stringify(params)}`;
  };

  useEffect(() => {
    const params: SdkSearchParams = getLocationParams(location);

    setFilter({
      state: params?.state ?? '',
      displayRent: {
        min: params?.minDisplayRent === '0' ? '' : params?.minDisplayRent ?? '',
        max: params?.maxDisplayRent === '0' ? '' : params?.maxDisplayRent ?? '',
      },
      bedroomCount: {
        min: params?.minBedroomCount ?? '',
      },
      bathroomCount: {
        min: params?.minBathroomCount ?? '',
      },
      availableNow: params?.availableNow === 'true',
    });
  }, [location]);

  const onRegisterSubmit = () => {
    GAEvent('User', 'Register');
  };

  const onLoginSubmit = () => {
    GAEvent('User', 'Login');
  };

  const onScheduleShowingStart = () => {
    GAEvent('User', 'Schedule showing');
  };

  const mainStyle: CSSProperties = {};
  const mapStyle: CSSProperties = {};
  if (config?.shadowRoot && !config?.onlySearchBar) {
    mainStyle.height = config.shadowMainHeight;
    mapStyle.height = '100%';
    mapStyle.width = '100%';
  }

  const hasResults = !!filteredUnits?.length;
  const shown = Math.min(MAX_UNITS_IN_LIST, visibleUnits.length);
  const total = filteredUnits?.length ?? 0;
  const sortOptions = getSortOptions();
  const sortValue =
    (sortBy &&
      sortOptions.find(({ sort }) => sort.key === sortBy.key && sort.direction === sortBy.direction)
        ?.value) ??
    '';

  return (
    <div
      className={classNames(classes.main, getSdkUnitListClassName('container'))}
      style={mainStyle}
    >
      <Toolbar
        filter={filter}
        onSearch={onSearch}
        setFilter={setFilter}
        states={stateKeys}
        onlySearchBar={config?.onlySearchBar}
      />
      {loading && (
        <Box
          p={8}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
          <Box mt={1}>
            <Typography variant="caption">Loading properties...</Typography>
          </Box>
        </Box>
      )}
      {!config?.onlySearchBar && hasResults && (
        <div className={classNames(classes.mapAndList, getSdkUnitListClassName('map-and-list'))}>
          <div className={classNames(classes.list, getSdkUnitListClassName('list'))}>
            {!!shown && !!total && (
              <div className={classNames(classes.header, getSdkUnitListClassName('header'))}>
                <Card>
                  <div
                    className={classNames(
                      classes.headerTitle,
                      getSdkUnitListClassName('headerTitle'),
                    )}
                  >
                    <div
                      className={classNames(
                        classes.headerSubtitle,
                        getSdkUnitListClassName('headerSubtitle'),
                      )}
                    >
                      <Typography component="span">
                        Viewing {shown} homes out of {total}
                      </Typography>
                    </div>
                    <div>
                      <Menu
                        title="Sort By"
                        value={sortValue}
                        onChange={(option) =>
                          setSortBy(
                            sortOptions.find((sortOption) => sortOption.value === option.value)
                              ?.sort ?? defaultSort,
                          )
                        }
                        options={sortOptions}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            )}

            <UnitList
              selectionEnabled={showMap}
              maxDisplay={MAX_UNITS_IN_LIST}
              onLoginSubmit={onLoginSubmit}
              onRegisterSubmit={onRegisterSubmit}
              onScheduleShowingStart={onScheduleShowingStart}
              setActivePlace={setActivePlace}
              units={visibleUnits}
              activePlace={activePlace}
            />
          </div>

          {showMap && (
            <div
              className={classNames(classes.map, getSdkUnitListClassName('map'))}
              style={mapStyle}
            >
              <UnitsMap
                points={points}
                activePlace={activePlace}
                setActivePlace={setActivePlace}
                mapData={mapData}
                updateMapData={setMapData}
              />
            </div>
          )}
        </div>
      )}
      {!config?.onlySearchBar && !hasResults && !loading && !blankPage && <NoResults />}
    </div>
  );
};

const withShowMapProp = (Component: any) => (props: any) => {
  const theme = useTheme();
  const showMap = useMediaQuery(theme.breakpoints.up('md'));
  return <Component showMap={showMap} {...props} />;
};

const AppWithStyles = withShowMapProp(withTheme(withStyles(styles, { name: 'App' })(memo(App))));

export default AppWithStyles;
